import { createFileRoute } from "@tanstack/react-router";

import { InstagramPageContent } from "@/components/site/Instagram";
import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { INSTAGRAM_URL } from "@/lib/instagram";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Instagram – Swedish International School in Estepona";
const DESCRIPTION =
  "Follow school life in Estepona and Marbella: photos and reels from lessons, sport, trips and events at the Swedish school on the Costa del Sol.";

export const Route = createFileRoute("/en/instagram")({
  loader: () => safeLoader("en.instagram", () => getInstagramFeed(), { posts: [], highlights: [] }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/en/instagram") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/instagram"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      The feed could not be loaded right now. Please try again shortly.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">Page not found.</div>
  ),
  component: InstagramPageEn,
});

function InstagramPageEn() {
  const { posts, highlights } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Instagram"
        title="School life in pictures"
        intro="On Instagram we share what happens during the school day – lessons, sport, trips and events. Here we collect the feed so you can follow us without an account."
      />

      <InstagramPageContent posts={posts} highlights={highlights} locale="en" />

      <Prose>
        <h2>Why we share our school days</h2>
        <p>
          Many families find us long before they move to Spain. Pictures from the school day give
          an honest impression of what it is like here: small groups, the Swedish curriculum and a
          playground the pupils can use for most of the year. We only publish things that have
          actually happened at school, and we always ask guardians for consent before pupils appear
          in a photo.
        </p>
        <h2>Follow us</h2>
        <p>
          The account is updated regularly during term time.{" "}
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
            Follow @swedish_international_school
          </a>{" "}
          for reels from sport, photos from the classrooms and information before each term starts.
        </p>
      </Prose>

      <CtaBand locale="en" />
    </>
  );
}
